<script lang="ts">
    import {
        faEllipsisV,
        faEye,
        faCaretRight,
        faChevronLeft,
        faChevronRight,
        faLink,
        faLock,
        faPlusCircle,
        faRefresh,
        faUserClock,
        faFilterCircleXmark,
    } from '@fortawesome/free-solid-svg-icons'
    import { onDestroy, onMount, tick } from 'svelte'
    import Fa from 'svelte-fa'
    import { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import { SchedulesRepository } from '../../../modules/schedulerApi/schedules/schedules.repository'
    import { SchedulesService } from '../../../modules/schedulerApi/schedules/schedules.service'
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    import updateLocale from 'dayjs/plugin/updateLocale'
    import localeData from 'dayjs/plugin/localeData'
    dayjs.extend(utc)
    dayjs.extend(localeData)
    dayjs.extend(updateLocale)
    dayjs.updateLocale('pt-BR', {
        weekdays: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
        weekdaysShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    })
    dayjs.locale('pt-BR')
    import ScheduleByColumn from './schedule-by-column.svelte'
    import Popover from '../../_common/popover.svelte'
    import AppointmentForm from '../appointment-form.svelte'
    import AppointmentSheet from '../v4/appointment-sheet.svelte'
    import WaitlistEntries from '../waitlist-entries.svelte'
    import Helper from '../../_common/helper.svelte'
    import { navigateTo } from 'yrv'
    import { sseCommand, mobileBarVisible } from '../../_common/stores'

    import Flatpickr from 'svelte-flatpickr'
    import 'flatpickr/dist/flatpickr.css' // import { Setting } from '../../modules/integrationsApi/settings/setting'
    import 'flatpickr/dist/themes/dark.css'
    import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
    import { ServicesRepository } from '../../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../../modules/posApi/services/services.service'
    import type { Service } from '../../../modules/posApi/services/service'
    import BlockForm from '../block-form.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { AppointmentsRepository } from '../../../modules/schedulerApi/appointments/appointments.repository'
    import { AppointmentsService } from '../../../modules/schedulerApi/appointments/appointments.service'
    import PosForm from '../../pos/pos-form.svelte'
    import CalendarNavigator from '../calendar-navigator.svelte'
    import SchedulesLinks from '../schedules-links.svelte'
    import type { ResourceLock } from '../../../modules/schedulerApi/resources-lock/resource-lock'
    import { AppointmentStatus, AppointmentStatusFriendly } from '../../../modules/schedulerApi/appointments/appointments.enum'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import { GridButton } from '../../_common/z/zgrid'
    import type { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import { ScheduleSlot } from '../../../modules/schedulerApi/schedules/scheduleSlot'
    import type { AppointmentResource } from '../../../modules/schedulerApi/appointments/appointment-resource'
    import type { CreateAppointmentDto } from '../../../modules/schedulerApi/appointments/dto/create-appointment.dto'
    import ScheduleByList from './schedule-by-list.svelte'
    import type { ZdropInterface } from '../../_common/z/zdrop.interface'
    import Zdrop from '../../_common/z/zdrop.svelte'
    import { User } from '../../../modules/usersApi/users/user'
    import { CompaniesRepository } from '../../../modules/usersApi/companies/companies.repository'
    import { CompaniesService } from '../../../modules/usersApi/companies/companies.service'
    import NotesColumn from './notes/notes-column.svelte'
    import { agendaNotes, clearAgendaNotesRange, handleAgendaNoteCommand, loadAgendaNotes, loadNotesPrefs, notesOfDay } from './notes/notes-store'

    // "Minhas notas" entra como uma pseudo-agenda: opção do "Por Agenda" no semanal e chip no mobile.
    // O id nunca vai para a API (ver loadScheduleByWeek e loadAllScheduleByDay).
    const NOTES_ID = '_minhas_notas_'
    // desktop, diário: coluna de notas à esquerda das agendas (toggle "Exibir minhas notas")
    let showNotes = false
    async function saveShowNotes() {
        await window.flags('scheduler-notes' + mobileSufix, true, showNotes)
    }
    $: notesSelected = selectedScheduleId === NOTES_ID
    $: notesWeekly = notesSelected && showRange !== 'daily'
    $: notesDaily = showRange === 'daily' && (isMobile ? notesSelected : showNotes)
    $: notesPending = selectedDate ? notesOfDay($agendaNotes, selectedDate).pending.length : 0
    let notesWeekDates: Date[] = []
    let schedulersHeight = 0

    // carrega as notas dos dias em exibição (scheduler-api) sempre que o intervalo muda: a semana no semanal
    // de notas, o dia no diário com a coluna visível. No mobile o diário sempre carrega, por causa do contador
    // do chip "Minhas notas". A chave em texto evita recarregar quando nada mudou de fato.
    $: notesRangeKey =
        notesWeekly && notesWeekDates.length
            ? notesDay(notesWeekDates[0]) + '|' + notesDay(notesWeekDates[notesWeekDates.length - 1])
            : showRange === 'daily' && selectedDate && (notesDaily || isMobile)
              ? notesDay(selectedDate) + '|' + notesDay(selectedDate)
              : ''
    $: if (notesRangeKey) loadAgendaNotes(notesRangeKey.split('|')[0], notesRangeKey.split('|')[1])
    // sem coluna de notas à vista: eventos de notas (SSE) não disparam recarga
    $: if (!notesRangeKey) clearAgendaNotesRange()
    function notesDay(date: Date) {
        return dayjs(date).format('YYYY-MM-DD')
    }
    // "por Fulano" nas notas públicas
    $: notesAuthors = Object.fromEntries(companyUsers.map((el) => [el.userId, el.userName]))

    let scheduleRep = new SchedulesRepository()
    let scheduleS = new SchedulesService(scheduleRep)

    let appointmentRep = new AppointmentsRepository()
    let appointmentS = new AppointmentsService(appointmentRep)

    let servicesR = new ServicesRepository()
    let servicesS = new ServicesService(servicesR)
    let services: Service[] = []

    let compR = new CompaniesRepository()
    let compS = new CompaniesService(compR)

    let showRange: 'daily' | 'weekly' | 'weekly-compact' = 'daily'
    let showView: 'fit' | 'list' = 'fit'
    let schedules: Schedule[] = []
    let start = 480
    let end = 1080
    let show = true
    let heightPerMinute: number = 0
    let url: string = ''

    let isMobile = screen.width <= 900
    let mobileSufix = isMobile ? '-mobile' : ''

    let lastScheduleChange: Date = new Date()
    let checkChangesHandle = null
    async function checkChanges() {
        try {
            if (lastScheduleChange) {
                const changes = await window.api.schedulers.schedules.changed(lastScheduleChange)
                for (const change of changes) {
                    // ajusta a data da última atualização com alteração
                    updateLastScheduleChange(change.lastAppointmentChange)
                    // se está exibindo todas as agendas, ou se a agenda em exibição é a que alterou...
                    if (showRange !== 'weekly' || change.scheduleId === selectedScheduleId) {
                        console.log('changes detected: ', changes, lastScheduleChange)
                        await loadSchedules(false)
                        break
                    }
                }
            }
        } catch (error) {
        } finally {
            startCheckChanges()
        }
    }

    function updateLastScheduleChange(lastAppointmentChange: Date) {
        if (lastAppointmentChange) {
            lastAppointmentChange = new Date(lastAppointmentChange)
            if (!lastScheduleChange || lastScheduleChange.getTime() <= lastAppointmentChange.getTime()) {
                lastScheduleChange = new Date(lastAppointmentChange)
                lastScheduleChange.setSeconds(lastScheduleChange.getSeconds() + 10)
            }
        }
    }

    function startCheckChanges() {
        if (checkChangesHandle) clearTimeout(checkChangesHandle)
        checkChangesHandle = setTimeout(checkChanges, 30000)
    }

    // substitui o polling do checkChanges (não é mais chamado, mas fica intacto acima): o scheduler-api
    // agora envia um comando via SSE a cada agendamento criado/editado/excluído/com status alterado.
    let lastHandledCommandDate: Date = new Date()
    let unsubscribeSseCommand = null

    const SCHEDULE_CHANGE_DEBOUNCE_MS = 800
    // teto pro debounce: sem isso, uma rajada sustentada de eventos (mais rápida que o debounce) reinicia o
    // timer pra sempre e loadSchedules() nunca chega a rodar — a tela fica "recebendo evento" sem nunca recarregar.
    const SCHEDULE_CHANGE_MAX_WAIT_MS = 3000
    let scheduleChangeDebounce = null
    let scheduleChangePendingSince: number = null

    function triggerScheduleReload() {
        if (scheduleChangeDebounce) clearTimeout(scheduleChangeDebounce)
        scheduleChangeDebounce = null
        scheduleChangePendingSince = null
        loadSchedules(false)
    }

    function scheduleReload() {
        const now = Date.now()
        if (!scheduleChangePendingSince) scheduleChangePendingSince = now
        if (now - scheduleChangePendingSince >= SCHEDULE_CHANGE_MAX_WAIT_MS) {
            triggerScheduleReload()
            return
        }
        if (scheduleChangeDebounce) clearTimeout(scheduleChangeDebounce)
        scheduleChangeDebounce = setTimeout(triggerScheduleReload, SCHEDULE_CHANGE_DEBOUNCE_MS)
    }

    function isSameDay(a: Date, b: Date) {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    }

    // a agenda + o horário do evento caem dentro do que está sendo exibido agora?
    function isWithinVisibleWindow(scheduleId: string, start: any) {
        if (!start) return true // payload sem start (retrocompat) — assume relevante, mesmo comportamento de antes
        if (!schedules.some((s) => s.scheduleId === scheduleId)) return false // agenda nem está na tela (filtro/grupo/pausada)
        const date = new Date(start)
        if (showRange?.includes('weekly')) {
            const weekStart = new Date(selectedDate)
            weekStart.setHours(0, 0, 0, 0)
            const weekEnd = new Date(weekStart)
            weekEnd.setDate(weekEnd.getDate() + 7)
            return date >= weekStart && date < weekEnd
        }
        return isSameDay(date, selectedDate)
    }

    // procura a cópia local do agendamento em qualquer agenda carregada agora (não só na do evento — se ele
    // mudou de agenda, a cópia local está sob a agenda ANTIGA, que não bate mais com value.scheduleId)
    function findLocalAppointment(appointmentId: string) {
        for (const schedule of schedules) {
            const found = schedule.appointments?.find((a) => a.appointmentId === appointmentId)
            if (found) return found
        }
        return null
    }

    function handleSseCommand(cmd: { act: string; value: any; date: Date }) {
        if (!cmd?.date || cmd.date <= lastHandledCommandDate) return
        // notas da agenda: o store decide se o intervalo em exibição é afetado (ver notes-store.ts)
        if (cmd.act === 'agenda-note-changed') {
            lastHandledCommandDate = cmd.date
            handleAgendaNoteCommand(cmd.value)
            return
        }
        if (cmd.act !== 'schedule-changed') return
        lastHandledCommandDate = cmd.date
        const value = cmd.value || {}
        console.log('schedule-changed command received', value, { showRange, selectedScheduleId })

        // 1) o novo horário/agenda cai no que está sendo exibido agora? (cobre criação, edição no lugar,
        //    status, exclusão e reagendamento PRA DENTRO da tela)
        let relevant = isWithinVisibleWindow(value.scheduleId, value.start)
        // 2) a cópia local desse agendamento (se existir na tela) ficou desatualizada em horário/agenda?
        //    (cobre reagendamento PRA FORA da tela — precisa recarregar pra ele sumir de onde estava)
        if (!relevant && value.start) {
            const local = findLocalAppointment(value.appointmentId)
            if (local && (+new Date(local.start) !== +new Date(value.start) || local.scheduleId !== value.scheduleId)) {
                relevant = true
            }
        }

        if (relevant) {
            scheduleReload()
        } else {
            console.log('schedule-changed command ignored — not relevant to what is currently shown')
        }
    }

    function showSunday(found: any) {
        //if (!selectedScheduleId) return
        //const found = availableSchedules.find((el) => el.scheduleId === selectedScheduleId)
        // se slots não estão configurados
        if (!found?.slots || found.slots.length === 0) {
            return true
        }
        // se slots estão configurados e há um slot ativo no domingo
        return found.slots[0].slots.some((el) => el.active)
    }

    async function loadSchedules(allowCache: boolean = true) {
        let promises = [loadAvaliableSchedules()] // como há cache, carregar antes roda junto das promises abaixo, e ao ser chamado logo em seguida usa-se o cache
        if (resources.length === 0) promises.push(loadResources())
        if (services.length === 0) promises.push(loadServices())
        if (scheduleGroups.length === 0) promises.push(loadScheduleGroups())
        if (companyUsers.length === 0) promises.push(loadCompanyUsers())
        if (categoryOptions.length === 0) promises.push(loadCategoryOptions())
        await Promise.all(promises)
        if (showRange === 'daily') {
            await loadAllScheduleByDay(allowCache)
        } else {
            await loadScheduleByWeek(allowCache)
        }
        setTimeout(loadHollidays)
        setTimeout(evaluateFab, 400)
    }

    // O botão "Agendar" é flutuante no mobile. Ele some ao chegar no fim da rolagem, para não cobrir
    // os botões do último agendamento do dia. Se o conteúdo nem rola, não há o que descobrir — ele fica.
    let schedulersEl: HTMLElement = null
    let hideFab = false
    // Mobile: os .controls (datas + chips) recolhem e reaparecem ACOMPANHANDO O DEDO, pixel a pixel,
    // sem animação própria (como a barra de endereço do Chrome no celular).
    // - Eles ficam sobrepostos ao topo da grade, que tem um espaçador da mesma altura (padding-top), e
    //   são deslocados por `top`. Assim controls e conteúdo andam juntos, 1:1 com a rolagem. Recolher
    //   por margem, como antes, faria o conteúdo andar o dobro do dedo.
    // - Não usar transform: ele vira bloco de contenção dos filhos position:fixed (botão "Agendar" e o
    //   menu de 3 pontos), que passariam a se posicionar dentro dos controls.
    // - Descendo, só a rolagem que passa do ponto de decisão recolhe. Subindo, qualquer rolagem revela —
    //   inclusive no fim da lista, mas agora sem salto, porque acompanha o dedo e não empurra a grade.
    const CONTROLS_DECISION_POINT = 200
    let controlsHeight = 0
    let controlsOffset = 0
    let lastSchedulersScroll = 0
    function evaluateControls() {
        if (!schedulersEl || window.innerWidth > 900) return
        const max = schedulersEl.scrollHeight - schedulersEl.clientHeight
        const raw = schedulersEl.scrollTop
        // elástico do iOS além do fim: não é intenção de rolar, só ignora
        if (raw > max) return
        const scrollTop = Math.max(0, raw)
        const delta = scrollTop - lastSchedulersScroll
        lastSchedulersScroll = scrollTop
        let next = controlsOffset
        if (delta > 0) {
            next += Math.min(delta, Math.max(0, scrollTop - CONTROLS_DECISION_POINT))
        } else {
            next += delta
        }
        next = Math.min(controlsHeight, Math.max(0, next))
        // nunca recolher mais do que já rolou, senão o espaçador do topo apareceria em branco
        next = Math.min(next, scrollTop)
        if (next !== controlsOffset) controlsOffset = next
    }

    function onSchedulersScroll() {
        evaluateControls()
        evaluateFab()
        scheduleNotesComposerIndex()
    }

    // Semanal de notas no mobile: o campo de rodapé fica só na coluna mais à esquerda visível, reavaliada
    // quando o arrasto para o lado termina (não durante, para não piscar). Com texto digitado numa
    // coluna, o campo fica preso a ela até ser enviado ou apagado.
    let notesLeftmostIndex = 0
    let notesDirtyIndex: number = null
    $: notesComposerIndex = notesDirtyIndex ?? notesLeftmostIndex
    let notesScrollEnd = null
    function scheduleNotesComposerIndex() {
        if (!notesWeekly || !isMobile) return
        if (notesScrollEnd) clearTimeout(notesScrollEnd)
        notesScrollEnd = setTimeout(evaluateNotesComposerIndex, 140)
    }
    function evaluateNotesComposerIndex() {
        if (!schedulersEl) return
        const left = schedulersEl.getBoundingClientRect().left
        const cols = Array.from(schedulersEl.querySelectorAll('.notes-col'))
        // primeira coluna com mais da metade à mostra
        const index = cols.findIndex((el) => {
            const r = el.getBoundingClientRect()
            return r.right - Math.max(r.left, left) > r.width / 2
        })
        if (index >= 0) notesLeftmostIndex = index
    }
    function onNotesDirty(index: number, dirty: boolean) {
        if (dirty) notesDirtyIndex = index
        else if (notesDirtyIndex === index) notesDirtyIndex = null
    }

    function evaluateFab() {
        if (!schedulersEl) return
        const rolavel = schedulersEl.scrollHeight - schedulersEl.clientHeight > 8
        const restante = schedulersEl.scrollHeight - schedulersEl.scrollTop - schedulersEl.clientHeight
        hideFab = rolavel && restante <= 24
    }

    let resources: ResourceLock[] = []
    async function loadResources() {
        resources = await window.api.schedulers.resourcesLock.getAll(undefined, 900)
    }

    let scheduleGroups: { scheduleGroupId: string; name: string; status: number }[] = []
    async function loadScheduleGroups() {
        scheduleGroups = await window.api.schedulers.scheduleGroups.getAll(undefined, 300)
        scheduleGroups = scheduleGroups.filter((el) => el.status === 100)
    }

    let companyUsers: User[] = []
    $: userOptions = companyUsers.map((el) => ({ value: el.userId, label: el.userName, group: 'Todos' }))
    async function loadCompanyUsers() {
        companyUsers = await compS.getCompanyUsers(300)
        selectedUsers = [...companyUsers.map((el) => el.userId)]
    }

    async function loadServices() {
        //services = await servicesS.getAll({ excluded: true, pageSize: 3000 }, 600)
        services = await servicesS.getServiceNames({ companyId: window.store.token.companyId, excluded: true, strict: true }, 600)
    }

    function serviceCategoryIdOf(serviceId: string) {
        return services.find((el) => el.serviceId === serviceId)?.serviceCategoryId || NO_CATEGORY
    }

    // atendente excluído (vínculo user/company é exclusão real — ver companies.repository.ts#findUsers)
    // nunca aparece em userOptions, então nunca poderia ser marcado/desmarcado no filtro. Sem esse
    // fallback, "todos selecionados" na prática nunca inclui ele, e o agendamento some da listagem sem
    // nenhum indício visual de que um filtro está ativo (ver conversa sobre "[usuário excluído]").
    function matchesUserFilter(item: { userId: string }) {
        if (selectedUsers.includes(item.userId)) return true
        return !userOptions.some((el) => el.value === item.userId)
    }

    // mesmo problema pra categoria de serviço removida: remove() é soft-delete (status=REMOVED) e não
    // limpa o serviceCategoryId dos serviços que apontavam pra ela (ver service-categories.service.ts#remove)
    // — a categoria some de categoryOptions mas o serviço continua ativo referenciando o id órfão.
    function matchesCategoryFilter(item: { serviceId: string }) {
        const categoryId = serviceCategoryIdOf(item.serviceId)
        if (selectedCategories.includes(categoryId)) return true
        return !categoryOptions.some((el) => el.value === categoryId)
    }

    // contagem de agendamentos por agenda, exibida nos chips do mobile. É calculada sobre TODAS as
    // agendas do dia (antes de reduzir a exibição à agenda escolhida), por isso o dia inteiro é sempre
    // carregado por completo — ver loadAllScheduleByDay.
    let scheduleCounts: { [scheduleId: string]: number } = {}
    let scheduleCountAll = 0
    function updateScheduleCounts(all: Schedule[]) {
        const counts = {}
        let total = 0
        for (const sched of all) {
            // applyFilters só olha status/userId/serviceId, que já vêm crus da API — não depende da hidratação
            const qtd = applyFilters(sched.appointments || []).length
            counts[sched.scheduleId] = qtd
            total += qtd
        }
        scheduleCounts = counts
        scheduleCountAll = total
    }

    // agendamento sem nenhum item (vem assim da API) passa nos filtros de profissional e de categoria:
    // não há o que casar, e `[].some()` é false — com TODOS os filtros marcados ele sumia da agenda sem
    // nenhum indício visual de filtro ativo. Mesma razão dos fallbacks de matchesUserFilter/Category.
    function applyFilters(aps: Appointment[]) {
        aps = aps.filter((el) => selectedStatuses.includes(el.status) || el.status == AppointmentStatus.agendamento_bloqueado)
        aps = aps.filter(
            (el) => el.status == AppointmentStatus.agendamento_bloqueado || !el.items?.length || el.items.some((item) => matchesUserFilter(item)),
        )
        aps = aps.filter(
            (el) => el.status == AppointmentStatus.agendamento_bloqueado || !el.items?.length || el.items.some((item) => matchesCategoryFilter(item)),
        )
        return aps
    }

    let selectedStatuses: string[] = []
    async function selectStatusFilter() {
        await loadSchedules()
    }

    let selectedUsers: string[] = []
    async function selectUsersFilter() {
        await loadSchedules()
    }

    let statusOptions: { value: string; label: string; group: string }[] = []
    function loadStatusOptions() {
        statusOptions = []
        for (const [key, value] of Object.entries(AppointmentStatusFriendly)) {
            statusOptions.push({ label: value, value: key, group: 'Todos' })
        }
        statusOptions = statusOptions
        selectedStatuses = [...statusOptions.map((el) => el.value)]
    }

    const NO_CATEGORY = '_none_'
    let selectedCategories: string[] = []
    async function selectCategoryFilter() {
        await loadSchedules()
    }

    let categoryOptions: { value: string; label: string; group: string }[] = []
    async function loadCategoryOptions() {
        const serviceCategories = await window.api.pos.serviceCategories.getAll(undefined, 600)
        categoryOptions = serviceCategories.map((el) => ({ value: el.serviceCategoryId, label: el.name, group: 'Todos' }))
        categoryOptions.push({ value: NO_CATEGORY, label: 'Sem categoria', group: 'Todos' })
        categoryOptions = categoryOptions
        selectedCategories = [...categoryOptions.map((el) => el.value)]
    }

    // act "limpar filtro" individual de cada Zinput "Por...": só aparece quando aquele filtro específico não está com tudo selecionado
    function clearFilterAct(callback: Function): GridButton[] {
        return [new GridButton({ icon: faFilterCircleXmark, title: 'Limpar filtro', callback })]
    }

    async function clearSchedulesGroupFilter() {
        selectedSchedulesGroup = schedGroupOptions.map((el) => el.value)
        await selectSchedulesGroup()
    }

    async function clearUsersFilter() {
        selectedUsers = userOptions.map((el) => el.value)
        await selectUsersFilter()
    }

    async function clearStatusFilter() {
        selectedStatuses = statusOptions.map((el) => el.value)
        await selectStatusFilter()
    }

    async function clearCategoryFilter() {
        selectedCategories = categoryOptions.map((el) => el.value)
        await selectCategoryFilter()
    }

    $: schedGroupActs = selectedSchedulesGroup.length !== schedGroupOptions.length ? clearFilterAct(clearSchedulesGroupFilter) : []
    $: usersActs = selectedUsers.length !== userOptions.length ? clearFilterAct(clearUsersFilter) : []
    $: statusActs = selectedStatuses.length !== statusOptions.length ? clearFilterAct(clearStatusFilter) : []
    $: categoryActs = selectedCategories.length !== categoryOptions.length ? clearFilterAct(clearCategoryFilter) : []

    let availableSchedules: Schedule[] = []
    let popupSchedules: { value: string; label: string }[] = []
    let schedOptions: { value: string; label: string }[] = []
    let selectedSchedulesGroup: string[] = []
    $: schedGroupOptions = availableSchedules.map((el) => ({
        value: el.scheduleId,
        label: el.label,
        group: scheduleGroups.find((g) => g.scheduleGroupId === el.scheduleGroupId)?.name || 'Outros',
    }))

    let schedViewOptions = []
    schedViewOptions.push({ value: 'fit', label: 'Blocos', group: 'Formato' })
    schedViewOptions.push({ value: 'list', label: 'Lista', group: 'Formato' })
    schedViewOptions.push({ value: 'daily', label: 'Diário', group: 'Visualização' })
    schedViewOptions.push({ value: 'weekly', label: 'Semanal', group: 'Visualização' })

    async function loadAvaliableSchedules() {
        if (availableSchedules.length === 0) {
            availableSchedules = await scheduleS.getAll({ discardSlots: true }, 60)
            availableSchedules = availableSchedules.filter((el) => el.status === 100)
            schedOptions = []
            for (const sched of availableSchedules) {
                schedOptions.push({ label: sched.label, value: sched.scheduleId })
            }
            schedOptions.unshift({ label: 'Minhas notas', value: NOTES_ID })
            schedOptions = schedOptions

            // carrega a última seleção múltipla salva; se não houver, marca todas as agendas por padrão
            const savedMultiSelection = await window.flags('scheduler-selected-multi' + mobileSufix, true)
            if (Array.isArray(savedMultiSelection) && savedMultiSelection.length > 0) {
                selectedSchedulesGroup = savedMultiSelection.filter((id) => availableSchedules.some((el) => el.scheduleId === id))
            } else {
                selectedSchedulesGroup = availableSchedules.map((el) => el.scheduleId)
            }
        }
        // carrega opções do combo (versão celular)
        popupSchedules = availableSchedules.map((el) => {
            return { value: el.scheduleId, label: el.label }
        })
        if (showRange === 'daily') {
            popupSchedules.unshift({ value: null, label: 'Todas' })
        }
        popupSchedules.unshift({ value: NOTES_ID, label: 'Minhas notas' })
        popupSchedules = popupSchedules
        await tick()

        // se nunca foi atribuída a última agenda utilizada...
        if (selectedScheduleId === null || selectedScheduleId === undefined) {
            const lastSelectedId = await window.flags('scheduler-selected' + mobileSufix, true)
            if (lastSelectedId === NOTES_ID || availableSchedules.find((el) => el.scheduleId === lastSelectedId)) {
                selectedScheduleId = lastSelectedId || ''
            }
        }

        // se não é daily, é obrigatório ter uma agenda selecionada...
        if (showRange !== 'daily' && availableSchedules.length > 0) {
            if (!selectedScheduleId) {
                selectedScheduleId = availableSchedules[0].scheduleId
            }
        }
    }

    function detectBestHeight() {
        if (isMobile) {
            heightPerMinute = 3
        } else {
            heightPerMinute = 2.1
        }
        let minimumInterval = 30
        for (const schedule of schedules) {
            if (minimumInterval > schedule.range) minimumInterval = schedule.range
        }
        if (minimumInterval >= 30) heightPerMinute -= 0.7
    }

    async function loadScheduleByWeek(allowCache: boolean = true) {
        for (const schedule of schedules) {
            schedule.appointments = []
        }
        schedules = schedules
        await tick()
        await loadAvaliableSchedules()
        //evaluateSunday() // determina se o domingo vai ou não aparecer para a agenda selecionada
        show = true
        let loadedSchedules = []
        selectedDate.setDate(selectedDate.getDate() - selectedDate.getDay()) // vai para o primeiro dia da semana, pois é semanal
        selectedDate = selectedDate
        await tick()
        redrawCalendar()
        // "Minhas notas" no semanal: uma coluna por dia, sem nada a buscar na API de agendas
        if (selectedScheduleId === NOTES_ID) {
            notesWeekDates = Array.from({ length: 7 }, (_, i) => dayjs(selectedDate).add(i, 'day').toDate())
            schedules = []
            show = false
            return
        }
        let _selectedDate = new Date(selectedDate)
        //
        let params = []
        let promises = []
        for (let index = 0; index < 7; index++) {
            // if (!showSunday && index === 0) {
            //     _selectedDate.setDate(_selectedDate.getDate() + 1)
            //     continue
            // }
            let from = _selectedDate.toISOString()
            let to = dayjs(new Date(new Date(from).setDate(new Date(from).getDate() + 1))).toISOString()
            let param = {
                from,
                to,
                appointments: true,
                scheduleId: selectedScheduleId,
                discardSlots: false, // window.api.cache.get('slots_' + selectedScheduleId, 600),
            }
            params.push(param)
            promises.push(scheduleS.getAll(param, allowCache ? 60 : undefined))
            _selectedDate.setDate(_selectedDate.getDate() + 1)
        }
        let _schedules: Schedule[] = (await Promise.all(promises)).flat()
        console.log('results', _schedules)
        let index = -1
        // schedules loop
        for (let schedule of _schedules) {
            index++
            if (schedule.status !== 100) continue
            let slots = window.api.cache.get('slots_' + schedule.scheduleId, 600)
            if (!slots) {
                window.api.cache.set('slots_' + schedule.scheduleId, schedule.slots, 'slots')
                slots = schedule.slots
            }
            schedule.slots = slots
            if (index === 0 && !showSunday(schedule)) continue
            start = schedule.startMinute
            end = schedule.endMinute
            schedule.selectedDate = new Date(params[index].from)
            schedule.label = ''
            loadedSchedules.push(schedule)
            for (const appointment of schedule.appointments) {
                for (let item of appointment.items) {
                    item.service = services.find((el) => el.serviceId === item.serviceId)
                }
                for (let item of appointment.resources) {
                    item.resourceLock = resources.find((el) => el.resourcesLockId === item.resourcesLockId)
                }
            }
            schedule.appointments = applyFilters(schedule.appointments)
        }
        schedules = loadedSchedules
        show = false
        await tick()
        promises = []
        for (const schedule of schedules) {
            promises.push(
                (async () => {
                    schedule.appointments = await window.api.utils.utils.assignPersonsFromObject(schedule.appointments)
                })(),
            )
        }
        await Promise.all(promises)

        schedules = schedules
        detectBestHeight()
    }

    async function loadAllScheduleByDay(allowCache: boolean = true) {
        clearCalendarColor()
        for (const schedule of schedules) {
            schedule.appointments = []
        }
        schedules = schedules
        const isMobileDaily = (await window.api.utils.utils.isMobile()) && screen.width < 900
        if (isMobileDaily) {
            await loadAvaliableSchedules()
        } else {
            selectedScheduleId = null
        }
        show = true
        let from = selectedDate.toISOString()
        let to = dayjs(new Date(new Date(from).setDate(new Date(from).getDate() + 1))).toISOString()
        let params = {
            from,
            to,
            appointments: true,
            // sempre carrega o dia inteiro de TODAS as agendas: os chips do mobile precisam da contagem
            // de cada uma. A exibição é reduzida à agenda escolhida logo abaixo.
            scheduleId: undefined,
            discardSlots: false, //window.api.cache.get('slots_all', 550),
        }
        let loadedSchedules: Schedule[] = await scheduleS.getAll(params, allowCache ? 60 : undefined)
        loadedSchedules = loadedSchedules.filter((el) => el.status === 100)
        updateScheduleCounts(loadedSchedules)
        if (!isMobileDaily) {
            loadedSchedules = loadedSchedules.filter((el) => selectedSchedulesGroup.includes(el.scheduleId))
        } else if (selectedScheduleId) {
            loadedSchedules = loadedSchedules.filter((el) => el.scheduleId === selectedScheduleId)
        }
        if (loadedSchedules.length > 0) start = loadedSchedules[0].startMinute
        let promises = []
        for (let schedule of loadedSchedules) {
            let slots = window.api.cache.get('slots_' + schedule.scheduleId, 600)
            if (!slots) {
                window.api.cache.set('slots_' + schedule.scheduleId, schedule.slots, 'slots')
                slots = schedule.slots
                window.api.cache.set('slots_all', true, 'slots')
            }
            schedule.slots = slots

            schedule.selectedDate = new Date(from)
            if (!selectedScheduleId) {
                if (schedule.startMinute < start) start = schedule.startMinute
                if (schedule.endMinute > end) end = schedule.endMinute
            } else {
                start = schedule.startMinute
                end = schedule.endMinute
            }
            for (const appointment of schedule.appointments) {
                for (let item of appointment.items) {
                    item.service = services.find((el) => el.serviceId === item.serviceId)
                }
                for (let item of appointment.resources) {
                    item.resourceLock = resources.find((el) => el.resourcesLockId === item.resourcesLockId)
                }
            }
            // filtra ANTES do primeiro render (como já é em loadSchedules): antes o filtro só rodava
            // depois do assignPersonsFromObject, que é uma ida ao servidor — a agenda pintava com os
            // agendamentos filtrados ainda dentro e eles piscavam e sumiam. E antes de montar a promise,
            // porque ela captura o array de appointments no momento em que é criada.
            schedule.appointments = applyFilters(schedule.appointments)
            promises.push(
                (async () => {
                    schedule.appointments = await window.api.utils.utils.assignPersonsFromObject(schedule.appointments)
                })(),
            )
        }
        schedules = loadedSchedules
        show = false
        await tick()
        await Promise.all(promises)
        schedules = schedules
        detectBestHeight()
    }

    let forceRedraw = false
    async function showDaily(save: boolean = true) {
        forceRedraw = true
        showRange = 'daily'
        resetSelectedDate()
        await loadSchedules()
        if (save) window.flags('scheduler-range' + mobileSufix, true, showRange)
        setTimeout(() => {
            forceRedraw = false
        }, 100)
    }

    async function showWeekly(save: boolean = true) {
        forceRedraw = true
        showRange = 'weekly'
        resetSelectedDate()
        await loadSchedules()
        if (save) window.flags('scheduler-range' + mobileSufix, true, showRange)
        setTimeout(() => {
            forceRedraw = false
        }, 100)
    }

    async function showWeeklyCompact(save: boolean = true) {
        forceRedraw = true
        showRange = 'weekly-compact'
        resetSelectedDate()
        await loadSchedules()
        if (save) window.flags('scheduler-range' + mobileSufix, true, showRange)
        setTimeout(() => {
            forceRedraw = false
        }, 100)
    }

    function showFit() {
        forceRedraw = true
        showView = 'fit'
        loadSchedules()
        window.flags('scheduler-view' + mobileSufix, true, showView)
        setTimeout(() => {
            forceRedraw = false
        }, 100)
    }

    function showList() {
        forceRedraw = true
        showView = 'list'
        loadSchedules()
        window.flags('scheduler-view' + mobileSufix, true, showView)
        setTimeout(() => {
            forceRedraw = false
        }, 100)
    }

    function evaluateView(ev) {
        if (ev.detail.value.includes('fit') && showView != 'fit') showFit()
        if (ev.detail.value.includes('list') && showView != 'list') showList()
        if (ev.detail.value.includes('daily') && showRange != 'daily') showDaily(true)
        if (ev.detail.value.includes('weekly') && showRange != 'weekly') showWeekly(true)
        console.log(ev.detail)
    }

    let clearSelection = ''
    function startSelecting(event) {
        clearSelection = event.detail
    }

    let appointment = null
    let invoiceId = null
    let comissionSource = ''
    async function onInvoiceChanged(event) {
        if (appointment?.appointmentId && event.detail.invoiceId) {
            await appointmentS.setPaid(appointment.appointmentId, {
                invoiceId: event.detail.invoiceId,
                comissionSource,
                voidComission: false,
                status: setAttended ? AppointmentStatus.agendamento_compareceu : undefined,
            })
            loadSchedules()
        }
        appointment = null
        invoiceId = null
    }

    function onInvoiceCanceled(event) {
        appointment = null
        invoiceId = null
    }

    let setAttended = false
    async function onInvoice(event) {
        setAttended = event.detail.attended
        if (event.detail.act === 'add') {
            invoiceId = null
            appointment = event.detail.appointment
        } else if (event.detail.act === 'edit') {
            invoiceId = event.detail.appointment.invoiceId
            appointment = null
        } else if (event.detail.act === 'open') {
            await appointmentS.setUnpaid(event.detail.appointment.appointmentId)
            await loadSchedules()
        } else if (event.detail.act === 'closed') {
            await appointmentS.setPaid(event.detail.appointment.appointmentId, {
                invoiceId: '*',
                comissionSource,
                voidComission: false,
                status: setAttended ? AppointmentStatus.agendamento_compareceu : undefined,
            })
            await loadSchedules()
        } else if (event.detail.act === 'closed-nocomission') {
            await appointmentS.setPaid(event.detail.appointment.appointmentId, {
                invoiceId: '*',
                comissionSource,
                voidComission: true,
                status: setAttended ? AppointmentStatus.agendamento_compareceu : undefined,
            })
            await loadSchedules()
        }
    }

    let dateRange: {
        start: Date
        end: Date
        scheduleId: string
        auto: boolean
        items?: { serviceId: string; userId?: string; parentServicesOnInvoicesId?: string }[]
    }
    async function stopSelecting(event) {
        dateRange = event.detail
    }

    async function evaluateOneClickAdd() {
        if (window.configs.scheduleOneClickAdd && !window.$.copy) {
            console.log('range', dateRange)
            await tick()
            newAppointment()
        }
    }

    function evaluateError(error) {
        if (error === "Cannot read properties of undefined (reading 'personId')") {
            error = 'Selecione ou cadastre o cliente'
        }
        window.error(error)
    }

    function showConfiguration() {
        navigateTo('/tools-scheduler/registration')
    }

    // ===================================

    let selectedDate = null
    let formatedDate = dayjs(new Date()).format('YYYY-MM-DD')
    function resetSelectedDate() {
        selectedDate = new Date(dayjs(new Date()).format('YYYY-MM-DD'))
        selectedDate.setMinutes(selectedDate.getMinutes() + selectedDate.getTimezoneOffset())
        formatedDate = dayjs(new Date()).format('YYYY-MM-DD')
    }

    resetSelectedDate()

    function handleChange(event) {
        const [selectedDates, dateStr] = event.detail
        dateRange = null
        setTimeout(async () => {
            await loadSchedules()
            redrawCalendar()
        }, 100)
    }

    let navigating = null
    async function goBackward() {
        // como no formato semanal é sempre o primeiro dia da semana, então -1 já joga para a semana anterior em caso de format !== 1
        if (showRange === 'daily') {
            selectedDate.setDate(selectedDate.getDate() - 1)
        } else {
            selectedDate.setDate(selectedDate.getDate() - 7)
            selectedDate.setDate(selectedDate.getDate() - selectedDate.getDay()) // apenas para acelerar o redraw
        }
        selectedDate = selectedDate
        if (navigating) clearTimeout(navigating)
        await tick()
        redrawCalendar()
        navigating = setTimeout(() => loadSchedules(), 350)
    }

    async function goForward() {
        if (showRange === 'daily') {
            // daily
            selectedDate.setDate(selectedDate.getDate() + 1)
        } else {
            selectedDate.setDate(selectedDate.getDate() + 7)
            selectedDate.setDate(selectedDate.getDate() - selectedDate.getDay()) // apenas para acelerar o redraw
        }
        selectedDate = selectedDate
        if (navigating) clearTimeout(navigating)
        await tick()
        redrawCalendar()
        navigating = setTimeout(() => loadSchedules(), 350)
    }

    function goToday() {
        resetSelectedDate()
        loadSchedules()
    }

    function handleChangeMobile(event) {
        dateRange = null
        selectedDate = new Date(formatedDate)
        selectedDate.setMinutes(new Date(formatedDate).getMinutes() + new Date(formatedDate).getTimezoneOffset())
        loadSchedules()
    }

    let selectedScheduleId = null
    async function selectSchedule(scheduleId) {
        forceRedraw = true
        console.log(1, scheduleId)
        selectedScheduleId = scheduleId
        await window.flags('scheduler-selected' + mobileSufix, true, scheduleId || '')
        console.log(2)
        setTimeout(loadSchedules, 100)
        console.log(3)
        setTimeout(() => {
            forceRedraw = false
            console.log('timout', show, schedules)
            console.log(4)
        }, 250)
    }

    async function selectSchedulesGroup() {
        await window.flags('scheduler-selected-multi' + mobileSufix, true, selectedSchedulesGroup)
        await loadSchedules()
    }

    async function onPaste(ev) {
        await evaluatePaste(ev.detail)
        loadSchedules()
    }

    async function checkIfIsFreeResources(resources: AppointmentResource[], currentAppointmentId: string, start: Date, end: Date) {
        if (!resources?.length) return true
        const lockedIds = await window.api.schedulers.resourcesLock.lockedIds(start, end, currentAppointmentId)
        return !resources.some((item) => lockedIds.includes(item.resourcesLockId))
    }

    function checkPermitedServices(scheduleId, items) {
        if (items.length === 0) return true
        console.log('items', scheduleId, items)
        // carrega serviços

        for (const item of items) {
            if (item.service.schedulers?.length && !item.service.schedulers.includes(scheduleId)) {
                return false
            }
        }
        return true
    }

    async function evaluatePaste(slot: ScheduleSlot) {
        let copied: Appointment = JSON.parse(JSON.stringify(window.$.copy.source))
        console.log('paste - received', window.$.copy.source)
        try {
            if (slot.start < new Date()) {
                if (!confirm('ATENÇÃO: a data/hora selecionada já passou.\nDeseja mesmo adicionar?')) {
                    return
                }
            }
            show = true
            let end = new Date(slot.start)
            let diff = dayjs(copied.end).diff(copied.start, 'm')
            end.setMinutes(end.getMinutes() + diff)
            if (!(await checkIfIsFreeResources(copied.resources, window.$.copy.cut ? copied.appointmentId : null, slot.start, end))) {
                window.error('Não é possível colar.\nUma sala ou equipamento necessário já está em uso neste horário.')
                return
            }
            if (!checkPermitedServices(slot.scheduleId, copied.items)) {
                window.error('Não é possível colar.\nUm ou mais serviços não podem ser agendados nesta agenda.')
                return
            }
            const dto: CreateAppointmentDto = {
                resources: copied.resources,
                comissionSource: await window.flags('schedulerComission'),
                source: 'erp',
                userId: window.store.token.userId,
                auto: copied.auto,
                end: end,
                items: copied.items.filter((el) => !!el),
                consumption: copied.consumption.filter((el) => !!el),
                groupId: copied.groupId,
                personId: copied.person?.personId,
                scheduleId: slot.scheduleId,
                start: slot.start,
                invoiceId: window.$.copy.cut ? copied.invoiceId : null,
                status:
                    copied.status === AppointmentStatus.agendamento_bloqueado
                        ? AppointmentStatus.agendamento_bloqueado
                        : window.$.copy.cut
                          ? copied.status
                          : AppointmentStatus.agendamento_criado,
                notes: copied.notes,
                summary: copied.person?.name + (copied.notes ? ' / ' + copied.notes : ''),
                repeat: null,
            }
            // assign category
            try {
                for (let item of dto.items) {
                    item.service = (await window.api.utils.utils.assignServiceCategoryFromObject([item.service]))[0]
                }
            } catch (error) {}
            try {
                copied = await window.api.schedulers.appointments.create(dto, { fitBlock: true })
            } catch (error) {
                if (error.statusCode === 409) {
                    if (!confirm('Já existe uma pessoa agendada neste horário. Deseja encaixar?')) return
                    copied = await window.api.schedulers.appointments.create(dto)
                }
            }
            console.log('paste - dto', dto)
            console.log('paste - persisted', copied)
            if (window.$.copy.cut) {
                await appointmentS.remove(window.$.copy.source.appointmentId)
            }
            console.log('paste result', copied)
        } catch (error) {
            evaluateError(error)
            show = false
        } finally {
            window.$.copy = null
        }
    }

    let _oldRange = null
    async function evaluateResize(ev) {
        return
        // se é semanal e reduziu o tamanho da agenda, converte para diário
        if (window.innerWidth <= 900) {
            if (showRange === 'daily') return
            _oldRange = showRange
            console.log('go daily')
            formatedDate = dayjs(selectedDate).format('YYYY-MM-DD')
            showDaily(false)
            return
        }
        // se é diário e aumentou o tamanho da agenda, converte para semanal (caso antes fosse semanal)
        if (window.innerWidth > 900) {
            if (_oldRange === 'weekly') {
                console.log('go weekly')
                _oldRange = null
                showWeekly(false)
            }
        }
    }

    // Mobile: quanto o campo de rodapé das notas precisa subir para não ficar atrás da barra inferior do app.
    // É medido, não fixo: o main do template tem height:100vh, e no celular o 100vh é maior que a área visível
    // enquanto a barra de endereço do navegador aparece — a base da grade fica abaixo da tela, e um valor fixo
    // (a altura da barra) deixava o campo escondido ao carregar. A barra é position:fixed na base visível.
    let notesComposerBottom = 0
    let lastBarHeight = 0
    function evaluateNotesComposerBottom() {
        if (!isMobile || !schedulersEl) return
        const bar = document.querySelector('.bar') as HTMLElement
        if (bar) lastBarHeight = bar.offsetHeight
        // offsetHeight (e não getBoundingClientRect) ignora o translate da animação de entrada/saída da barra
        const visibleBottom = window.innerHeight - ($mobileBarVisible ? lastBarHeight : 0)
        notesComposerBottom = Math.max(0, Math.round(schedulersEl.getBoundingClientRect().bottom - visibleBottom))
    }
    function scheduleNotesComposerBottom() {
        evaluateNotesComposerBottom()
        // de novo depois que a barra termina de entrar/sair e o layout assenta
        setTimeout(evaluateNotesComposerBottom, 150)
    }
    $: $mobileBarVisible, schedulersEl, notesSelected, scheduleNotesComposerBottom()

    // Mobile, digitando nas notas: como no chat do WhatsApp, a área da agenda encolhe para terminar no topo do
    // teclado. O campo de rodapé, que é sticky na base da coluna, fica colado no teclado, e as notas continuam
    // roláveis logo acima. O teclado não encolhe o layout (nem no iPhone, nem no padrão do Chrome do Android),
    // só a área visível: por isso a medida vem do visualViewport — mesma técnica do zinput-expander.
    // Vale só com um campo de notas em foco E o teclado de fato aberto. Não basta o foco: recolhendo o teclado
    // pelo botão do sistema, o campo continua em foco, e a agenda ficava encolhida com o campo atrás da barra
    // inferior. Tirar o foco nesse caso seria arriscado (teclado flutuante/físico ou tela dividida não encolhem a
    // área visível, e a digitação seria interrompida); ligar pelo teclado aberto erra, no pior caso, para o normal.
    let notesTyping = false
    let notesTypingHeight: number = null
    let notesTypingFrame = null
    // o teclado cobre bem mais que isso; a barra de endereço do navegador aparecendo/sumindo, não
    const NOTES_KEYBOARD_MIN_PX = 120
    function isNotesComposerFocused() {
        return isMobile && !!(document.activeElement as HTMLElement)?.classList?.contains('composer-input')
    }
    function onNotesFocusChange() {
        // depois do evento: durante o focusout o activeElement ainda é o campo que está saindo
        setTimeout(() => {
            const typing = isNotesComposerFocused()
            if (typing === notesTyping) return
            notesTyping = typing
            scheduleNotesTypingHeight()
        })
    }
    function scheduleNotesTypingHeight() {
        if (notesTypingFrame) cancelAnimationFrame(notesTypingFrame)
        notesTypingFrame = requestAnimationFrame(evaluateNotesTypingHeight)
    }
    async function evaluateNotesTypingHeight() {
        notesTypingFrame = null
        if (!schedulersEl) return
        // o campo pode sair da tela sem focusout (ex.: no semanal, a coluna deixou de ser a ativa); o teclado
        // fecha, o visualViewport avisa e a conferência cai aqui
        if (notesTyping && !isNotesComposerFocused()) notesTyping = false
        const vv = window.visualViewport
        const before = schedulersEl.clientHeight
        let next: number = null
        const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight
        const keyboardOpen = !!vv && window.innerHeight - visibleBottom >= NOTES_KEYBOARD_MIN_PX
        if (notesTyping && keyboardOpen) {
            const top = schedulersEl.getBoundingClientRect().top
            next = Math.max(120, Math.round(visibleBottom - top))
        }
        if (next === notesTypingHeight) return
        // posição guardada antes: ao crescer, o navegador corta a rolagem no novo máximo antes desta correção
        const scrollBefore = schedulersEl.scrollTop
        notesTypingHeight = next
        await tick()
        // mantém à vista o trecho junto do campo: rola o quanto a área encolheu (e devolve quando ela cresce)
        schedulersEl.scrollTop = scrollBefore + before - schedulersEl.clientHeight
    }

    onMount(async () => {
        window.addEventListener('resize', evaluateResize)
        window.addEventListener('resize', scheduleNotesComposerBottom)
        window.visualViewport?.addEventListener('resize', scheduleNotesComposerBottom)
        document.addEventListener('focusin', onNotesFocusChange)
        document.addEventListener('focusout', onNotesFocusChange)
        window.visualViewport?.addEventListener('resize', scheduleNotesTypingHeight)
        window.visualViewport?.addEventListener('scroll', scheduleNotesTypingHeight)
        loadStatusOptions()
        comissionSource = await window.flags('schedulerComission')
        // if (screen.width <= 900) {
        //     showRange = 'daily'
        //     showView = 'fit'
        // }
        //if (screen.width > 900) {
        showRange = await window.flags('scheduler-range' + mobileSufix, true)
        // retrocompatibilidade com a versão anterior
        if (!showRange) {
            const format = await window.flags('scheduler-format' + mobileSufix, true)
            if (format === 1) showRange = 'daily'
            if (format === 2) showRange = 'weekly'
            if (showRange) await window.flags('scheduler-range' + mobileSufix, true, showRange)
            if (!showRange) showRange = 'daily'
        }
        if (isMobile) {
            showView = 'fit'
        } else {
            showView = await window.flags('scheduler-view' + mobileSufix, true)
            if (showView === undefined || showView === null) showView = 'fit'
        }
        //}
        showNotes = !!(await window.flags('scheduler-notes' + mobileSufix, true))
        await loadNotesPrefs()

        resetSelectedDate()
        await loadSchedules()
        // startCheckChanges() // substituído pelo comando via SSE (handleSseCommand) — ver comentário acima
        unsubscribeSseCommand = sseCommand.subscribe(handleSseCommand)
        if (screen.width <= 900 && showRange?.includes('weekly')) {
            setTimeout(() => {
                window.location.hash = '#isToday_true'
            }, 500)
        }
    })

    onDestroy(async () => {
        if (checkChangesHandle) clearTimeout(checkChangesHandle)
        if (scheduleChangeDebounce) clearTimeout(scheduleChangeDebounce)
        if (unsubscribeSseCommand) unsubscribeSseCommand()
        window.removeEventListener('resize', scheduleNotesComposerBottom)
        window.visualViewport?.removeEventListener('resize', scheduleNotesComposerBottom)
        document.removeEventListener('focusin', onNotesFocusChange)
        document.removeEventListener('focusout', onNotesFocusChange)
        window.visualViewport?.removeEventListener('resize', scheduleNotesTypingHeight)
        window.visualViewport?.removeEventListener('scroll', scheduleNotesTypingHeight)
        if (notesTypingFrame) cancelAnimationFrame(notesTypingFrame)
    })

    let _showLinks = false
    function showLinks() {
        _showLinks = true
    }

    let currentCalendarNode = null
    function clearCalendarColor() {
        if (currentCalendarNode) {
            let current = currentCalendarNode
            for (let index = 1; index <= 6; index++) {
                current = current.nextElementSibling
                current?.classList.remove('next')
            }
            currentCalendarNode = null
        }
    }
    function redrawCalendar() {
        clearCalendarColor()
        currentCalendarNode = document.querySelector('.flatpickr-day.selected')
        let current = currentCalendarNode
        for (let index = 1; index <= 6; index++) {
            current = current.nextElementSibling
            current?.classList.add('next')
        }
    }

    // ===============================================
    let appointmentId = null
    let personId = null
    function editAppointment(event) {
        personId = event.detail.personId
        appointmentId = event.detail.appointmentId
        showAppointment = true
    }

    let blockAppointmentId = null
    function editBlock(event) {
        blockAppointmentId = event.detail.appointmentId
        showBlock = true
    }

    async function appointmentChanged() {
        await loadSchedules()
    }

    let blink = []
    async function appointmentClosed(ev) {
        personId = null
        showAppointment = false
        showBlock = false
        appointmentId = null
        blockAppointmentId = null
        console.log('retorno', ev.detail)
        blink = ev.detail || []
        setTimeout(() => {
            blink = []
        }, 2500)
    }

    function refreshAppointment() {
        loadSchedules()
    }

    let showBlock = false
    function newBlock() {
        if (!dateRange?.scheduleId) {
            evaluateError(['Primeiro selecione um horário na agenda'])
            return
        }
        blockAppointmentId = null
        showBlock = true
    }

    let showAppointment = false
    function newAppointment() {
        window.$.copy = null
        if (!dateRange?.scheduleId) {
            evaluateError(['Primeiro selecione um horário na agenda'])
            return
        }
        appointmentId = null
        blockAppointmentId = null
        showAppointment = true
    }

    function cancelNew() {
        personId = null
        showAppointment = false
        showBlock = false
        appointmentId = null
        blockAppointmentId = null
    }

    let showWaitlist = false
    function openWaitlist() {
        showWaitlist = true
    }

    function onWaitlistSlotSelected(event) {
        dateRange = {
            start: event.detail.start,
            end: event.detail.end,
            scheduleId: event.detail.scheduleId,
            auto: false,
            items: event.detail.items,
        }
        personId = event.detail.personId
        appointmentId = null
        showWaitlist = false
        newAppointment()
    }

    let fpOptions = {
        inline: true,
        locale: Portuguese,
        onDayCreate: async (dObj, dStr, fp, dayElem) => {
            await new Promise((resolve) => {
                if (hollidays.length > 0) resolve(null)
                setTimeout(resolve, 7000)
            })
            if (hollidays.some((el) => el.month === dayElem.dateObj.getMonth() + 1 && el.day === dayElem.dateObj.getDate())) {
                dayElem.classList.add('-is-holliday')
            }
        },
        onReady: function (selectedDates, dateStr, instance) {
            setTimeout(() => {
                instance.redraw()
                redrawCalendar()
            }, 500)
        },
    }

    let hollidays: { month: number; day: number; date: Date; label: string }[] = []
    let lastYear = null
    async function loadHollidays() {
        let selectedYear = selectedDate?.getFullYear() || new Date().getFullYear()
        if (hollidays.length > 0 && lastYear === selectedYear) return
        const ibge = await window.api.schedulers.schedules.hollidays(selectedYear) //adapters.BrasilAPI.IBGEGetHolidays(selectedYear)
        if (ibge && ibge.length > 0) {
            hollidays = ibge.map((el) => {
                const date = new Date(el.date)
                date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
                return { month: parseInt(el.date.split('-')[1]), day: parseInt(el.date.split('-')[2]), date: date, label: el.name }
            })
            lastYear = selectedYear
        }
        setTimeout(() => evaluateShowHolliday(selectedDate), 500)
    }

    function evaluateShowHolliday(date: Date) {
        if (showRange === 'daily') {
            const month = date.getMonth() + 1
            const day = date.getDate()
            const result = hollidays.filter((el) => el.month === month && el.day === day)
            selectedHollidays = result
            return
        }
        const sunday = new Date(date)
        sunday.setDate(date.getDate() - date.getDay()) // vai para o primeiro dia da semana
        const saturday = new Date(sunday)
        saturday.setDate(sunday.getDate() + 7)
        const result = hollidays.filter((el) => el.date.getTime() >= sunday.getTime() && el.date.getTime() < saturday.getTime())
        console.log('res ho', result)
        selectedHollidays = result
    }

    let selectedHollidays: any[] = []
    $: {
        evaluateShowHolliday(selectedDate)
    }

    function setFormat(type: 'weekly' | 'daily' | 'weekly-compact') {
        if (type === 'daily') {
            showDaily()
        } else if (type === 'weekly') {
            showWeekly()
        } else if (type === 'weekly-compact') {
            showWeeklyCompact()
        }
    }

    let actionMenu: ZdropInterface = {
        format: 'square',
        position: 'flow',
        positionMargin: '4px',
        icon: faEllipsisV,
        buttons: [
            {
                callback: openWaitlist,
                label: 'Lista de espera',
            },
            {
                callback: newBlock,
                label: 'Bloqueios',
            },
            {
                callback: showLinks,
                label: 'Links da agenda',
            },
        ],
    }

    let formatMenu: ZdropInterface = {
        format: 'square',
        position: 'flow',
        positionMargin: '4px',
        icon: faEye,
        buttons: [
            {
                callback: () => setFormat('daily'),
                label: 'Diário',
            },
            {
                callback: () => setFormat('weekly'),
                label: 'Semanal',
            },
            {
                callback: () => setFormat('weekly-compact'),
                label: 'Semanal compacto',
            },
        ],
    }
</script>

{#if _showLinks}
    <Popover title="Agenda Online" width="1200px" height="155px" on:close={() => (_showLinks = false)}>
        <SchedulesLinks />
    </Popover>
{/if}

{#if showWaitlist}
    <Popover title="Lista de espera" width="1100px" slow={true} on:close={() => (showWaitlist = false)}>
        <WaitlistEntries on:close={() => (showWaitlist = false)} on:selectSlot={onWaitlistSlotSelected} />
    </Popover>
{/if}

{#if showAppointment}
    <Popover
        title={isMobile ? (appointmentId ? 'Editar agendamento' : 'Novo agendamento') : 'Agendamento'}
        width="1080px"
        height="80vh"
        slow={true}
        on:close={() => {
            cancelNew()
        }}
    >
        <!-- o mobile usa a folha nova (v4); o desktop e as outras telas que abrem o agendamento
             seguem no formulário atual -->
        {#if isMobile}
            <AppointmentSheet
                dates={dateRange}
                on:refresh={refreshAppointment}
                on:change={appointmentChanged}
                on:close={appointmentClosed}
                on:cancel={cancelNew}
                bind:appointmentId
                {personId}
            />
        {:else}
            <AppointmentForm
                dates={dateRange}
                on:refresh={refreshAppointment}
                on:change={appointmentChanged}
                on:close={appointmentClosed}
                on:cancel={cancelNew}
                bind:appointmentId
                {personId}
            />
        {/if}
    </Popover>
{/if}
{#if showBlock}
    <Popover
        title="Bloqueio"
        width="800px"
        height="300px"
        on:close={() => {
            cancelNew()
        }}
    >
        <BlockForm dates={dateRange} on:change={appointmentChanged} on:close={cancelNew} on:cancel={cancelNew} bind:appointmentId={blockAppointmentId} />
    </Popover>
{/if}

{#if appointment || invoiceId}
    <Popover
        title="Venda"
        width="1300px"
        height="90vh"
        on:close={() => {
            appointment = null
            invoiceId = null
        }}
    >
        <PosForm {appointment} {invoiceId} on:change={onInvoiceChanged} on:cancel={onInvoiceCanceled} />
    </Popover>
{/if}

<!-- --notes-composer-bottom: o campo de rodapé das notas fica acima da barra inferior do app (ver evaluateNotesComposerBottom);
     digitando, a área já termina no topo do teclado, que cobre a barra (ver evaluateNotesTypingHeight) -->
<div
    class="root-schedule schedule-content"
    style="--controls-h:{controlsHeight}px;--controls-offset:{controlsOffset}px;--notes-composer-bottom:{notesTypingHeight !== null
        ? 0
        : notesComposerBottom}px"
>
    <div class="h-screen">
        {#if showRange !== 'daily' && availableSchedules.length > 0}
            <div>Agenda: {availableSchedules.find((el) => el.scheduleId === selectedScheduleId)?.label}</div>
            <div>Data Inicial: {dayjs(selectedDate).format('DD/MM/YYYY')}</div>
        {:else}
            <div>Data: {dayjs(selectedDate).format('DD/MM/YYYY')}</div>
        {/if}
    </div>
    <div class="controls scroll-dark h-print" bind:clientHeight={controlsHeight}>
        <div class="h-full cal-nav-pers">
            <CalendarNavigator showActive={showRange !== 'weekly-compact'} bind:value={formatedDate} on:change={handleChangeMobile} />
        </div>
        <div class="act">
            <div class="h-full"><Zdrop menu={formatMenu}></Zdrop></div>
            <div class="h-full"><Zdrop menu={actionMenu}></Zdrop></div>

            <!-- mobile: faixa de agendas rolável na horizontal, no lugar do antigo select -->
            <div class="schedule-chips h-full">
                {#each popupSchedules as chip (chip.value || '_todas_')}
                    <button
                        type="button"
                        class="schedule-chip"
                        class:active={(chip.value || null) === (selectedScheduleId || null)}
                        on:click={() => selectSchedule(chip.value)}
                    >
                        <span class="-label">{chip.label}</span>
                        <span class="-count">{chip.value === NOTES_ID ? notesPending : chip.value ? scheduleCounts[chip.value] || 0 : scheduleCountAll}</span>
                    </button>
                {/each}
            </div>
            <!-- <select class="form-select" bind:value={selectedScheduleId} on:change={() => selectSchedule(selectedScheduleId)}>
                        <optgroup label="Selecione uma agenda">
                            {#each availableSchedules as schedule}
                                <option value={schedule.scheduleId}>{schedule.label}</option>
                            {/each}
                        </optgroup>
                    </select> -->

            <div class="calendar h-mobile" class:week={showRange !== 'daily'}>
                <div class="mb-3">
                    <Flatpickr options={fpOptions} bind:value={selectedDate} bind:formattedValue={formatedDate} on:change={handleChange} />
                    <div class="btn-calendar">
                        <button on:click={goBackward}><Fa icon={faChevronLeft} /></button>
                        <button on:click={goToday} class="btn-calendar_today"><Fa icon={faRefresh} /> Hoje</button>
                        <button on:click={goForward}><Fa icon={faChevronRight} /></button>
                    </div>
                </div>
                {#if selectedHollidays.length > 0}
                    <div class="calendar-hollidays mb-3">
                        <!-- <div class="-title">Feriados nacionais de {window.api.utils.utils.getMonthName(selectedDate.getMonth() + 1)}:</div> -->
                        {#each selectedHollidays as holliday}
                            <!-- {#if holliday.month === selectedDate.getMonth() + 1} -->

                            <div class="-item">
                                <div class="-day">
                                    {dayjs(holliday.date).utc().format('DD')}
                                </div>
                                <div class="-label">{holliday.label}</div>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
            {#if window.store.token.permissions.scheduler.scheduler.write}
                <!-- no mobile este bloco vira botão de ação flutuante (ver .agendar-act no @media) -->
                <div class="d-flex agendar-act" class:fab-hidden={hideFab || notesSelected} class:fab-bar-hidden={!$mobileBarVisible}>
                    <Zbutton color="primary" grow={true} on:click={newAppointment} icon={faPlusCircle} mobileHideLabel={false} label="Agendar"></Zbutton>
                </div>
                <div class="d-flex h-mobile mt-2">
                    <Zbutton color="secondary" grow={true} on:click={openWaitlist} icon={faUserClock} mobileHideLabel={true} label="Espera"></Zbutton>
                    <Zbutton on:click={newBlock} icon={faLock}></Zbutton>
                    <Zbutton on:click={showLinks} icon={faLink}></Zbutton>
                </div>
            {/if}
        </div>
        <div class="appointments-format h-mobile">
            {#if showRange === 'daily'}
                <!-- no semanal as notas são escolhidas no "Por Agenda" (opção "Minhas notas") -->
                <Zinput class="mt-4" type="toggle" label="Exibir minhas notas" bind:value={showNotes} on:change={saveShowNotes}></Zinput>
            {/if}
            <Zinput
                skin="outline"
                class="mt-4"
                type="radioselect"
                label="Exibição"
                options={schedViewOptions}
                value={[showView, showRange]}
                on:change={evaluateView}
            ></Zinput>
            {#if showRange?.includes('weekly')}
                <Zinput
                    skin="outline"
                    class="mt-4"
                    type="select"
                    label="Por Agenda"
                    options={schedOptions}
                    value={selectedScheduleId}
                    on:change={(ev) => selectSchedule(ev.detail.value)}
                ></Zinput>
            {/if}
            {#if showRange === 'daily'}
                <!-- o "Por Agenda" já cobre a seleção por grupo: o cabeçalho de cada grupo marca/desmarca todas as
                     agendas dele de uma vez (ver zinput-groupselect), então o filtro separado de grupo foi removido -->
                <Zinput
                    skin="outline"
                    class="mt-4"
                    type="groupselect"
                    label="Por Agenda"
                    options={schedGroupOptions}
                    bind:value={selectedSchedulesGroup}
                    acts={schedGroupActs}
                    on:change={selectSchedulesGroup}
                ></Zinput>
            {/if}
            <Zinput
                skin="outline"
                class="mt-2"
                type="groupselect"
                label="Por Atendente"
                options={userOptions}
                bind:value={selectedUsers}
                acts={usersActs}
                on:change={selectUsersFilter}
            ></Zinput>
            <Zinput
                skin="outline"
                class="mt-2"
                type="groupselect"
                label="Por status"
                options={statusOptions}
                bind:value={selectedStatuses}
                acts={statusActs}
                on:change={selectStatusFilter}
            ></Zinput>
            <Zinput
                skin="outline"
                class="mt-2"
                type="groupselect"
                label="Por categoria"
                options={categoryOptions}
                bind:value={selectedCategories}
                acts={categoryActs}
                on:change={selectCategoryFilter}
            ></Zinput>
        </div>

        <!-- a legenda de cores saiu daqui: cada agendamento agora exibe a própria badge de status -->
    </div>
    <div class="schedulers_outer">
        <div
            class="schedulers scroll-dark schedulers-format-{showRange}"
            class:schedulers-notes-only={notesWeekly || (isMobile && notesSelected)}
            style={notesTypingHeight !== null ? `height:${notesTypingHeight}px` : undefined}
            bind:this={schedulersEl}
            bind:clientHeight={schedulersHeight}
            on:scroll={onSchedulersScroll}
        >
            {#if !forceRedraw && notesWeekly}
                {#each notesWeekDates as notesDate, i (notesDate.getTime())}
                    <!-- desktop: campo só na coluna sob o mouse; mobile: só na mais à esquerda visível -->
                    <NotesColumn
                        date={notesDate}
                        weekly={true}
                        authors={notesAuthors}
                        composerMode={isMobile ? 'external' : 'hover'}
                        composerActive={i === notesComposerIndex}
                        on:dirty={(ev) => onNotesDirty(i, ev.detail)}
                    />
                {/each}
            {:else if !forceRedraw}
                <!-- notas à esquerda de tudo, inclusive da coluna de horários da Lista: não têm horário,
                     e a borda dela divide os dois tipos de recurso -->
                {#if notesDaily && selectedDate}
                    <NotesColumn date={selectedDate} fullWidth={isMobile} pinned={!isMobile} height={schedulersHeight} authors={notesAuthors} />
                {/if}
                {#if showView === 'fit'}
                    {#each schedules as schedule, index}
                        <ScheduleByColumn
                            fullWidth={schedules?.length === 1}
                            widthFactor={showRange === 'daily' ? schedule.widthFactor || 1 : 1}
                            {blink}
                            on:startSelecting={startSelecting}
                            {index}
                            {clearSelection}
                            {schedule}
                            groups={scheduleGroups}
                            {start}
                            {end}
                            on:paste={onPaste}
                            {heightPerMinute}
                            showTime={true}
                            on:stopSelecting={stopSelecting}
                            on:invoice={onInvoice}
                            on:edit={editAppointment}
                            on:edit-block={editBlock}
                            on:click={evaluateOneClickAdd}
                        />
                    {/each}
                {:else if schedules.length > 0}
                    <ScheduleByList
                        {heightPerMinute}
                        {schedules}
                        widthFactors={showRange === 'daily' ? Object.fromEntries(schedules.map((el) => [el.scheduleId, el.widthFactor || 1])) : {}}
                        groups={scheduleGroups}
                        on:stopSelecting={stopSelecting}
                        on:paste={onPaste}
                        on:invoice={onInvoice}
                        on:edit={editAppointment}
                        on:edit-block={editBlock}
                        on:click={evaluateOneClickAdd}
                    ></ScheduleByList>
                {/if}
            {/if}
        </div>
    </div>
</div>
<LoadSpinner {show} delay={800} />

<style>
    .root-schedule {
        height: 100%;
        overflow: hidden;
        display: flex;
        /* padding-top: 8px; */
    }

    .schedule-content {
        margin: 0px;
        border-radius: 0px;
        background-color: unset;
    }

    .helper-center-object {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: center;
    }

    .controls {
        display: flex;
        flex-direction: column;
        flex: 0 0 266px;
        overflow-y: auto;
        overflow-x: hidden;
        border-radius: 0px;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        padding: 11px 13px;
        align-items: center;
    }
    .controls .act {
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    .controls .act .d-flex {
        gap: 4px;
    }

    /* Faixa de agendas do mobile: rola na horizontal com o dedo, sobre o mesmo verde da faixa de datas */
    .controls .schedule-chips {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: 1 1 10px;
        min-width: 0;
        padding: 0px 8px;
        overflow-x: auto;
        overflow-y: hidden;
        /* sem -webkit-overflow-scrolling: touch — ver a mesma observação no zsheet.svelte */
        scrollbar-width: none;
    }
    .controls .schedule-chips::-webkit-scrollbar {
        display: none;
    }
    .controls .schedule-chip {
        display: flex;
        align-items: center;
        gap: 5px;
        flex: 0 0 auto;
        border: 1px solid #ffffff3d;
        background: #ffffff1f;
        color: #ffffff;
        border-radius: 999px;
        padding: 5px 11px;
        font-size: 13px;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
    }
    .controls .schedule-chip .-count {
        font-size: 11px;
        font-weight: 600;
        color: #ffffffab;
    }
    .controls .schedule-chip.active {
        background: #ffffff;
        border-color: #ffffff;
        color: #11603e;
        font-weight: 600;
    }
    .controls .schedule-chip.active .-count {
        color: #11603e99;
    }
    .controls .appointments-format {
    }

    .btn-calendar {
        display: flex;
        background-color: #333333;
        border-radius: 0px 0px 8px 8px;
        overflow: hidden;
    }
    .btn-calendar > button {
        flex: 1 1 10px;
        padding: 8px 6px 4px 6px;
        background-color: #333333;
        color: #ccc;
        border: 0px;
    }
    .btn-calendar .btn-calendar_today {
        border-left: 1px solid #77777766;
        border-right: 1px solid #77777766;
    }
    .btn-calendar > button:hover {
        background-color: #222;
    }

    .schedulers_outer {
        flex-grow: 1;
        margin: 12px 12px 12px 0px;
        border-radius: 7px;
        overflow: hidden;
        box-shadow: 1px 1px 6px #33333330;
        border: 1px solid #222222;
    }
    .schedulers {
        flex-grow: 1;
        overflow: auto;
        display: flex;
        padding: 0px;
        height: 100%;
        user-select: none;

        padding: 0px 0px;
        /* flex-direction: column; */
        padding-bottom: 127px !important;
        background-color: #383838;
        grid-auto-flow: column;
        scroll-snap-type: x mandatory;
    }
    /* só colunas de notas (sem "Agendar" flutuante): sem a folga do fim, as colunas vão até a base e o
       campo de rodapé delas encosta na borda de baixo */
    .schedulers.schedulers-notes-only {
        padding-bottom: 0px !important;
    }

    .controls .cal-nav-pers {
        width: 100% !important;
        background-color: #157c53;
        padding-bottom: 8px;
    }

    .controls .appointments-format {
        width: 100%;
    }
    .controls .appointments-format .btn-group button {
        flex: 1 1 10px;
    }

    .calendar :global(.-is-holliday) {
        color: yellow;
    }
    .calendar-hollidays {
        background-color: #99999930;
        padding: 6px;
        border-radius: 8px;
        font-size: 13px;
    }
    .calendar-hollidays .-title {
        margin-bottom: 6px;
        font-size: 14px;
    }
    .calendar-hollidays .-item {
        display: flex;
        display: flex;
        align-items: center;
    }

    .calendar-hollidays .-item:not(:last-child) {
        margin-bottom: 4px;
    }

    .calendar-hollidays .-day {
        color: yellow;
        background-color: #99999960;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 25px;
        flex: 0 0 25px;
    }
    .calendar-hollidays .-label {
        padding-left: 6px;
    }

    :global(.schedulers-format-weekly-compact *) {
        font-size: 12px !important;
    }

    :global(.schedulers-format-weekly-compact .notes-badge) {
        display: none;
    }

    :global(.schedulers-format-weekly-compact .schedule-col) {
        min-width: 160px !important;
    }

    :global(.schedulers-format-weekly .schedule-col) {
        max-width: 340px !important;
    }

    .appointments-format .-types {
        width: 100%;
    }

    @media (max-width: 900px) {
        .root-schedule {
            flex-direction: column;
            padding-top: 0px;
        }
        .schedule-content {
            border-radius: 0px;
        }

        .schedulers_outer {
            margin: 0px;
            border-radius: 0px !important;
            border-top: 0px;
        }
        /* controls sobrepostos ao topo da grade e deslocados pela rolagem, sem transição (ver
           evaluateControls). --controls-h e --controls-offset vêm do .root-schedule. */
        .root-schedule {
            position: relative;
        }
        .controls {
            position: absolute;
            top: calc(-1 * var(--controls-offset, 0px));
            left: 0px;
            right: 0px;
            z-index: 5;
            flex: 0 0 auto;
            margin-bottom: 0px;
            background-color: #99999920 !important;
            border-radius: 0px !important;
            padding: 0px !important;
            border-top: 0px solid #99999910 !important;
        }
        .controls .act {
            display: flex;
            flex-direction: unset;
            margin-top: 0px;
            /* mesmo verde da faixa de datas acima, para os dois blocos lerem como um cabeçalho só */
            background-color: #157c53;
        }

        /* "Agendar" vira botão de ação flutuante, acima da barra inferior do app (61px de altura).
           Ele some ao chegar no fim da rolagem — ver evaluateFab. */
        .controls .agendar-act {
            position: fixed;
            right: 14px;
            bottom: 75px;
            z-index: 4;
            width: auto;
            flex-grow: 0;
            border-radius: 999px;
            box-shadow: 0px 4px 14px #0000005c;
            transition:
                opacity 0.18s ease,
                transform 0.18s ease,
                bottom 0.1s linear;
        }
        /* barra inferior oculta (store mobileBarVisible): desce junto, em vez de flutuar sobre o vazio.
           Mesma duração/curva da barra. */
        .controls .agendar-act.fab-bar-hidden {
            bottom: 14px;
        }
        .controls .agendar-act.fab-hidden {
            opacity: 0;
            transform: translateY(14px);
            pointer-events: none;
        }
        .controls .act .d-flex {
            flex-direction: row-reverse;
        }
        .controls > * {
            flex-grow: 1;
        }

        .calendar {
            flex-grow: 1;
            display: flex;
        }

        .schedulers > :global(.list) {
            scroll-snap-align: start;
        }
        /* espaçador do tamanho dos controls: rola junto com o conteúdo, e é o que faz controls e grade
           andarem 1:1. O cabeçalho fixo das colunas gruda logo abaixo da parte visível dos controls.
           O top do sticky é medido a partir da borda INTERNA ao padding do contêiner (que já está
           controls-h abaixo do topo), por isso basta subir o que os controls recolheram. */
        .schedulers {
            padding-top: var(--controls-h, 0px) !important;
            --sticky-top: calc(-1 * var(--controls-offset, 0px));
        }
        :global(.root-schedule .act .zbutton) {
            border-radius: 0px;
        }
        /* depois da regra acima de propósito, para arredondar só o botão flutuante */
        :global(.root-schedule .agendar-act .zbutton) {
            border-radius: 999px !important;
            height: 48px;
            padding-left: 17px;
            padding-right: 19px;
        }
    }

    @media print {
        .schedulers {
            overflow: unset;
            display: unset;
            padding: 0px 0px;
            height: unset;
        }
    }
</style>
